import { Outlet, Link, useLocation } from "react-router-dom";
import EtatConnexion from "@/components/EtatConnexion";
import { useAuth } from "@/lib/hooks/useAuth";
import { useEcranEtroit } from "@/lib/hooks/useEcranEtroit";
import { logout } from "@/lib/firebase/auth";
import { LayoutDashboard, Store, Users, ShoppingCart, Boxes, MessageSquare, LogOut, ChevronRight, Settings, UserCircle, Bell, ClipboardCheck, FileSpreadsheet, Calculator, Wallet, Link2, Bike, FileText } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { collection, query, where, onSnapshot, orderBy, Timestamp } from "firebase/firestore";
import { db } from "@/lib/firebase/config";
import { COLLECTIONS } from "@/lib/constants";
import NotificationPrompt from "@/components/NotificationPrompt";
import NewOrderAlarm from "@/components/NewOrderAlarm";
import BoutonLangue from "@/components/BoutonLangue";

export default function AdminLayout() {
  // Une seule copie de la page montee (01/11/2026) : voir useEcranEtroit.
  const etroit = useEcranEtroit();
  const { user } = useAuth();
  const location = useLocation();
  const [unreadCount, setUnreadCount] = useState(0);
  const [showAudioPrompt, setShowAudioPrompt] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const previousCountRef = useRef(-1);
  const audioEnabledRef = useRef(
    localStorage.getItem("audioNotifications") !== "false"
  );

  // 🔔 Demander l'autorisation audio au premier chargement
  useEffect(() => {
    const hasAskedPermission = localStorage.getItem("audioPermissionAsked");
    if (!hasAskedPermission) {
      setTimeout(() => setShowAudioPrompt(true), 2000);
    } else if (hasAskedPermission === "granted" && audioRef.current) {
      audioRef.current.load();
      audioEnabledRef.current = true;
    }
  }, []);

  const handleEnableAudio = () => {
    if (audioRef.current) {
      audioRef.current.play().then(() => {
        audioRef.current!.pause();
        audioRef.current!.currentTime = 0;
        audioEnabledRef.current = true;
        localStorage.setItem("audioNotifications", "true");
        localStorage.setItem("audioPermissionAsked", "granted");
        setShowAudioPrompt(false);
      }).catch(() => {
        localStorage.setItem("audioNotifications", "false");
        localStorage.setItem("audioPermissionAsked", "denied");
        setShowAudioPrompt(false);
      });
    }
  };

  const handleDenyAudio = () => {
    audioEnabledRef.current = false;
    localStorage.setItem("audioNotifications", "false");
    localStorage.setItem("audioPermissionAsked", "denied");
    setShowAudioPrompt(false);
  };

  const handleLogout = async () => {
    await logout();
  };

  // 🔔 Écouter les messages non lus (sauf si on est sur la page Messages)
  useEffect(() => {
    // 🔥 Ne pas écouter si on est sur la page Messages
    if (location.pathname === "/admin/messaging") {
      setUnreadCount(0);
      previousCountRef.current = -1;
      return;
    }

    const threeDaysAgo = Timestamp.fromDate(new Date(Date.now() - 3 * 24 * 60 * 60 * 1000));

    const q = query(
      collection(db, COLLECTIONS.CONVERSATIONS),
      where("isActive", "==", true),
      where("lastMessageAt", ">=", threeDaysAgo),
      orderBy("lastMessageAt", "desc") // 🔥 Utiliser l'index existant
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const total = snapshot.docs.reduce((sum, doc) => {
        const data = doc.data();
        return sum + (data.unreadCount || 0);
      }, 0);

      console.log(`📊 Unread count: ${total}, previous: ${previousCountRef.current}, audio enabled: ${audioEnabledRef.current}`);

      // 🔔 Jouer le son si nouveau message (ignorer le premier chargement)
      if (previousCountRef.current === -1) {
        // Premier chargement, juste initialiser sans jouer
        console.log("🔄 Premier chargement, initialisation sans son");
        previousCountRef.current = total;
      } else if (total > previousCountRef.current && audioEnabledRef.current) {
        // Vraiment un nouveau message
        console.log("🔔 Nouveau message détecté, jouer le son...");
        audioRef.current?.play()
          .then(() => console.log("✅ Son joué"))
          .catch((e) => console.log("❌ Audio play failed:", e.message));
        previousCountRef.current = total;
      } else {
        previousCountRef.current = total;
      }

      setUnreadCount(total);
    });

    return () => unsubscribe();
  }, [location.pathname]);

  const navItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/stores", label: "Stores", icon: Store },
    { path: "/admin/orders", label: "Commandes", icon: ShoppingCart },
    // Les livraisons du site et leurs livreurs (01/11/2026) : le tableau de
    // bord livreurs, dans le meme menu que le reste.
    { path: "/admin/livreurs", label: "Livreurs", icon: Bike },
    { path: "/admin/releves-fournisseurs", label: "Relevés fournisseurs", icon: FileText },
    { path: "/admin/suivi", label: "Performance", icon: ClipboardCheck },
    { path: "/admin/customers", label: "Clients", icon: Users },
    { path: "/admin/messaging", label: "Messages", icon: MessageSquare },
    { path: "/admin/users", label: "Utilisateurs", icon: UserCircle },
    { path: "/admin/inventory", label: "Inventaire", icon: Boxes },
    { path: "/admin/notifications", label: "Notifications", icon: Bell },
    { path: "/admin/import-glovo", label: "Import Glovo", icon: FileSpreadsheet },
    // La caisse du responsable commande : juste avant la comptabilité,
    // parce que c'est là qu'on va vérifier ce qui en est sorti.
    { path: "/admin/caisse", label: "Caisse", icon: Wallet },
    { path: "/admin/comptabilite", label: "Comptabilité", icon: Calculator },
    // Les liens de prix envoyes aux fournisseurs d'un nouveau magasin.
    { path: "/admin/liens-fournisseurs", label: "Liens fournisseurs", icon: Link2 },
    { path: "/admin/settings", label: "Paramètres", icon: Settings },
  ];

  // Menu simplifié pour mobile (seulement 4 items essentiels)
  const mobileNavItems = [
    { path: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/orders", label: "Commandes", icon: ShoppingCart },
    { path: "/admin/messaging", label: "Messages", icon: MessageSquare },
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* 🔔 Audio de notification (caché) */}
      <audio ref={audioRef} src="/sounds/notification.mp3" preload="auto" />

      {/* Notification Permission Prompt */}
      {/* 🚨 Alerte sonore a l'arrivee d'une commande */}
      <NewOrderAlarm lienCommandes="/admin/orders" />

      <NotificationPrompt />

      {/* 🔔 Popup de demande d'autorisation audio */}
      {showAudioPrompt && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in slide-in-from-bottom-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">
                Activer les notifications sonores ?
              </h3>
              <p className="text-gray-600 mb-6">
                Recevez un son de notification lorsque vous avez un nouveau message WhatsApp
              </p>
              <div className="flex gap-3">
                <button
                  onClick={handleDenyAudio}
                  className="flex-1 px-4 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-medium hover:bg-gray-50 transition-colors"
                >
                  Plus tard
                </button>
                <button
                  onClick={handleEnableAudio}
                  className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-xl font-medium hover:from-blue-600 hover:to-blue-700 transition-colors shadow-lg shadow-blue-500/30"
                >
                  Activer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {/* Mobile : une barre en haut, pour le seul bouton qui doit etre
          atteignable depuis n'importe quel ecran — la langue. */}
      <div className="md:hidden sticky top-0 z-40 bg-white border-b border-gray-200 px-4 py-2 flex items-center justify-between">
        <span className="font-bold text-gray-900">Natura Beldi</span>
        <BoutonLangue />
      </div>

      {/* Mobile: Bottom Navigation - Menu simplifié (4 items) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 z-50 shadow-lg">
        <div className="flex justify-around">
          {mobileNavItems.map((item) => {
            const Icon = item.icon;
            const isActive = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex-1 flex flex-col items-center justify-center py-3 px-2 min-h-[70px] transition-colors relative ${
                  isActive
                    ? "text-primary"
                    : "text-gray-500"
                }`}
              >
                <div className="relative">
                  <Icon className={`w-6 h-6 ${isActive ? "scale-110" : ""} transition-transform`} />
                  {/* 🔔 Badge pour Messages */}
                  {item.path === "/admin/messaging" && unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                      {unreadCount > 9 ? "9+" : unreadCount}
                    </span>
                  )}
                </div>
                <span className="text-xs mt-1 text-center leading-tight">{item.label}</span>
                {isActive && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-primary rounded-t-full"></div>
                )}
              </Link>
            );
          })}
          <button
            onClick={handleLogout}
            className="flex-1 flex flex-col items-center justify-center py-3 px-2 min-h-[70px] transition-colors text-red-600 hover:bg-red-50"
            title="Déconnexion"
          >
            <LogOut className="w-6 h-6" />
            <span className="text-xs mt-1 text-center leading-tight">Déco</span>
          </button>
        </div>
      </nav>

      {/* Desktop: Sidebar moderne */}
      <div className="hidden md:flex w-full overflow-hidden">
        <aside className="w-72 flex-shrink-0 bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 min-h-screen flex flex-col shadow-2xl">
          {/* Logo et titre */}
          <div className="p-6 border-b border-white/10">
            <div className="flex items-center gap-3 mb-2">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
                <span className="text-white font-bold text-lg">N</span>
              </div>
              <div>
                <h1 className="text-xl font-bold text-white">Natura Beldi</h1>
                <p className="text-xs text-gray-400 font-medium">Management Platform</p>
              </div>
              <div className="ml-auto">
                <BoutonLangue sombre />
              </div>
            </div>
            <div className="mt-3 px-3 py-2 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
              <p className="text-xs text-gray-400">Connecté en tant que</p>
              <p className="text-sm font-semibold text-white truncate">{user?.name || "Admin"}</p>
              {!etroit && <EtatConnexion sombre />}
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`group flex items-center gap-3 px-4 py-3 rounded-xl transition-all relative ${
                    isActive
                      ? "bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-lg shadow-blue-500/30"
                      : "text-gray-400 hover:text-white hover:bg-white/5"
                  }`}
                >
                  {isActive && (
                    <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 bg-white rounded-r-full"></div>
                  )}
                  <div className="relative">
                    <Icon className={`w-5 h-5 ${isActive ? "text-white" : "text-gray-400 group-hover:text-white"} transition-colors`} />
                    {/* 🔔 Badge pour Messages */}
                    {item.path === "/admin/messaging" && unreadCount > 0 && (
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center animate-pulse">
                        {unreadCount > 9 ? "9+" : unreadCount}
                      </span>
                    )}
                  </div>
                  <span className="font-medium text-sm">{item.label}</span>
                  {isActive && (
                    <ChevronRight className="w-4 h-4 ml-auto" />
                  )}
                </Link>
              );
            })}
          </nav>

          {/* Footer avec déconnexion */}
          <div className="p-4 border-t border-white/10">
            <button
              onClick={handleLogout}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-red-500/10 hover:bg-red-500/20 text-red-400 hover:text-red-300 rounded-xl transition-all border border-red-500/20 hover:border-red-500/30"
            >
              <LogOut className="w-4 h-4" />
              <span className="font-medium text-sm">Déconnexion</span>
            </button>
          </div>
        </aside>
        <main className="flex-1 min-w-0 p-6 pb-20 md:pb-6 overflow-hidden">
          {!etroit && <Outlet />}
        </main>
      </div>

      {/* Mobile: Main Content */}
      <div className="md:hidden pb-20">
        {/* En affichage etroit, la barre laterale n'existe pas : sans cette
            bande, ni le nom ni l'etat de connexion ne sont visibles. */}
        {etroit && <EtatConnexion nom={user?.name} barre />}
        <main className="p-4">
          {etroit && <Outlet />}
        </main>
      </div>
    </div>
  );
}

